import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  Folder,
  FolderCog,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import { type FormEvent, Fragment, useEffect, useRef, useState } from 'react'
import {
  BulkActionBar,
  EmptyTable,
  GroupHeaderRow,
  Pager,
  RowCheckbox,
  SelectAllCheckbox,
  SortableHeader,
  TableToolbar,
} from '../components/data-table'
import { EntityAccessPanel } from '../components/entity-access-panel'
import { ManualGroupPanel } from '../components/manual-group-panel'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { api, type Customer, type FibuFirma } from '../lib/api'
import { CreationAttempt } from '../lib/creation-attempt'
import { useDataTable } from '../lib/data-table'
import { type GroupOption, useGroupedTable } from '../lib/grouped-table'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

const CUSTOMER_GROUP_OPTIONS: GroupOption[] = [
  { key: 'none', label: 'Keine' },
  { key: 'city', label: 'Stadt' },
  { key: 'country', label: 'Land' },
]

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomersPage,
})

export function CustomersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [showArchived, setShowArchived] = useState(false)
  const [dialog, setDialog] = useState<{
    open: boolean
    customer: Customer | null
    session: number
  }>({
    open: false,
    customer: null,
    session: 0,
  })
  const opening = useRef(0)
  const openDialog = async (customer: Customer | null) => {
    const generation = ++opening.current
    try {
      const fresh = customer
        ? (
            await queryClient.fetchQuery({
              queryKey: ['customer-edit', customer.id, generation],
              queryFn: () => api.getCustomer(customer.id),
              staleTime: 0,
              gcTime: 0,
            })
          ).customer
        : null
      if (generation === opening.current)
        setDialog((current) => ({ open: true, customer: fresh, session: current.session + 1 }))
    } catch (error) {
      if (generation === opening.current) notify.error((error as Error).message)
    }
  }
  const closeDialog = (session: number) =>
    setDialog((current) =>
      current.session === session ? { ...current, open: false, customer: null } : current,
    )
  // Mobile-Shell (<640): Suche-Sheet hinterm Bottom-Slot; Titel nur
  // standalone — eingebettet (/stammdaten-Tab) gehört er der Host-Seite.
  const [searchSheet, setSearchSheet] = useState(false)
  useMobilePageTitle(embedded ? null : 'Kunden')
  const [accessPanel, setAccessPanel] = useState<{
    open: boolean
    customer: Customer | null
    bulkIds: string[] | null
  }>({
    open: false,
    customer: null,
    bulkIds: null,
  })
  const [groupPanel, setGroupPanel] = useState<
    { open: boolean; mode: 'assign'; ids: string[] } | { open: boolean; mode: 'manage' }
  >({ open: false, mode: 'manage' })

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['customers', { archived: showArchived }],
    queryFn: () => api.listCustomers(showArchived),
    enabled: !!meData?.user,
  })
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'customer'],
    queryFn: () => api.listEntityGroups('customer'),
    enabled: !!meData?.user,
  })

  const customers = data?.customers ?? []
  const customerGroups = groupsData?.groups ?? []
  const table = useDataTable<Customer>(
    customers,
    {
      name: 'name',
      city: 'billingCity',
      vatId: 'billingVatId',
      createdAt: (c) => new Date(c.createdAt),
    },
    {
      searchableFields: [
        'name',
        'notes',
        'billingCity',
        'billingZip',
        'billingVatId',
        'billingContactName',
      ],
      defaultSortKey: 'name',
      defaultSortDir: 'asc',
      defaultPageSize: 25,
      storageKey: 'customers',
    },
  )

  const isAdminUser = meData?.user?.role === 'admin'
  const grouped = useGroupedTable<Customer>(table, {
    pageKey: 'customers',
    options: CUSTOMER_GROUP_OPTIONS,
    manualGroups: customerGroups,
    manualGroupsEnabled: isAdminUser,
    getId: (c) => c.id,
    groupBy: (c, mode) => {
      if (mode === 'city') {
        const city = c.billingCity?.trim() ?? ''
        return city ? { key: city, label: city } : { key: '__none__', label: 'Ohne Stadt' }
      }
      if (mode === 'country') {
        const co = c.billingCountry?.trim() ?? ''
        return co ? { key: co, label: co } : { key: '__none__', label: 'Ohne Land' }
      }
      return null
    },
  })

  const queryClient = useQueryClient()
  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteCustomers(ids),
    onSuccess: (r) => {
      table.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      notify.success(`${r.deleted} Kunden gelöscht`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  const isAdmin = meData.user.role === 'admin'

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche → Sheet + ＋ Neuer Kunde —
          Handler identisch zu den Kopf-/Toolbar-Controls darunter. */}
      <MobileBottomSlots>
        <BarSlot
          label="Suche"
          icon={<Search className="h-5 w-5" />}
          active={table.search !== ''}
          ariaLabel="Kunden durchsuchen"
          ariaHasPopup
          onClick={() => setSearchSheet(true)}
        />
        <BarAccentPlus ariaLabel="Neuer Kunde" onClick={() => openDialog(null)} />
      </MobileBottomSlots>

      {/* Suche-Sheet (<640): bindet dieselbe table.search wie die
          Toolbar-Suche — Treffer filtern live hinter dem Sheet. */}
      <SidePanel open={searchSheet} onClose={() => setSearchSheet(false)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={table.search}
            onChange={(e) => table.setSearch(e.target.value)}
            placeholder="Kunden durchsuchen …"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Kunden"
        actions={
          <Button onClick={() => openDialog(null)} size="lg">
            <Plus className="h-4 w-4" /> Neuer Kunde
          </Button>
        }
      />

      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
        groupBy={{
          value: grouped.groupBy,
          options: grouped.options,
          onChange: grouped.setGroupBy,
          onExpandAll: grouped.expandAll,
          onCollapseAll: grouped.collapseAll,
          allCollapsed: grouped.allCollapsed,
        }}
        rightSlot={
          <>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setGroupPanel({ open: true, mode: 'manage' })}
                title="Gruppen verwalten"
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors"
              >
                <FolderCog className="h-3.5 w-3.5" />
                <span className="text-xs whitespace-nowrap">Gruppen verwalten</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-text-tertiary hover:text-accent transition-colors whitespace-nowrap"
            >
              {showArchived ? 'Aktive zeigen' : 'Archivierte zeigen'}
            </button>
          </>
        }
      />

      <TableShell className="rounded-2xl bg-bg-elevated/40">
        {isLoading ? (
          <div className="px-6 py-12 text-center text-text-secondary">Lade …</div>
        ) : customers.length === 0 ? (
          <EmptyTable message="Noch keine Kunden. Leg den ersten an." />
        ) : table.rows.length === 0 ? (
          <EmptyTable message="Keine Treffer." hint="Suche oder Filter anpassen." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/40">
              <tr className="text-left">
                {isAdmin && (
                  <th className="pl-5 pr-2 py-2.5 w-10">
                    <SelectAllCheckbox
                      checked={table.allOnPageSelected}
                      indeterminate={table.someOnPageSelected}
                      onToggle={table.toggleAllOnPage}
                    />
                  </th>
                )}
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Ort"
                    sortKey="city"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="USt-IdNr"
                    sortKey="vatId"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Angelegt"
                    sortKey="createdAt"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5 w-px" aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {grouped.disabled
                ? table.rows.map((c) => (
                    <CustomerRow
                      key={c.id}
                      customer={c}
                      isAdmin={isAdmin}
                      selected={table.isSelected(c.id)}
                      onSelect={() => table.toggleRow(c.id)}
                      onEdit={() => openDialog(c)}
                      onManageAccess={() =>
                        setAccessPanel({ open: true, customer: c, bulkIds: null })
                      }
                    />
                  ))
                : grouped.groups.map((g) => {
                    const ids = g.items.map((c) => c.id)
                    const selectedInGroup = ids.filter((id) => table.isSelected(id)).length
                    const groupChecked = selectedInGroup > 0 && selectedInGroup === ids.length
                    const groupIndeterminate = selectedInGroup > 0 && selectedInGroup < ids.length
                    return (
                      <Fragment key={g.key}>
                        <GroupHeaderRow
                          label={g.label}
                          count={g.items.length}
                          restrictedCount={g.restrictedCount}
                          collapsed={g.collapsed}
                          onToggle={() => grouped.toggleGroup(g.key)}
                          colSpan={isAdmin ? 6 : 5}
                          groupChecked={isAdmin ? groupChecked : undefined}
                          groupIndeterminate={isAdmin ? groupIndeterminate : undefined}
                          color={g.color}
                          muted={g.muted}
                          onToggleAll={
                            isAdmin
                              ? () => {
                                  // If everyone in this group is already
                                  // selected, untick them. Otherwise tick all.
                                  const shouldClear =
                                    selectedInGroup === ids.length && ids.length > 0
                                  for (const id of ids) {
                                    const isOn = table.isSelected(id)
                                    if (shouldClear && isOn) table.toggleRow(id)
                                    else if (!shouldClear && !isOn) table.toggleRow(id)
                                  }
                                }
                              : undefined
                          }
                        />
                        {!g.collapsed &&
                          g.items.map((c) => (
                            <CustomerRow
                              key={c.id}
                              customer={c}
                              isAdmin={isAdmin}
                              selected={table.isSelected(c.id)}
                              onSelect={() => table.toggleRow(c.id)}
                              onEdit={() => openDialog(c)}
                              onManageAccess={() =>
                                setAccessPanel({ open: true, customer: c, bulkIds: null })
                              }
                            />
                          ))}
                      </Fragment>
                    )
                  })}
            </tbody>
          </table>
        )}
      </TableShell>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />

      {isAdmin && (
        <BulkActionBar
          selectionCount={table.selectionCount}
          filteredRows={table.filteredRows}
          onClear={table.clearSelection}
          onSelectAllFiltered={table.selectAllFiltered}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setGroupPanel({
                open: true,
                mode: 'assign',
                ids: Array.from(table.selectedIds),
              })
            }
          >
            <Folder className="h-3.5 w-3.5" /> Zur Gruppe
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setAccessPanel({
                open: true,
                customer: null,
                bulkIds: Array.from(table.selectedIds),
              })
            }
          >
            <Lock className="h-3.5 w-3.5" /> Freigabe
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={bulkDelete.isPending}
            onClick={async () => {
              const ids = Array.from(table.selectedIds)
              const ok = await confirm({
                title: `${ids.length} Kunden löschen?`,
                description:
                  'Projekte werden mitgelöscht, Time-Entries verlieren ihren Project-Bezug. Diese Aktion ist unumkehrbar.',
                variant: 'danger',
                confirmLabel: 'Endgültig löschen',
              })
              if (ok) bulkDelete.mutate(ids)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </Button>
        </BulkActionBar>
      )}

      <CustomerDialog
        key={dialog.session}
        open={dialog.open}
        customer={dialog.customer}
        isAdmin={isAdmin}
        onClose={() => closeDialog(dialog.session)}
      />
      <EntityAccessPanel
        open={accessPanel.open}
        onClose={() => setAccessPanel({ open: false, customer: null, bulkIds: null })}
        resourceType="customer"
        resourceId={accessPanel.customer?.id ?? null}
        resourceIds={accessPanel.bulkIds ?? undefined}
        resourceName={
          accessPanel.bulkIds
            ? `${accessPanel.bulkIds.length} Kunden`
            : (accessPanel.customer?.name ?? '')
        }
      />
      {isAdmin &&
        (groupPanel.mode === 'assign' ? (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="customer"
            mode="assign"
            resourceIds={groupPanel.ids}
          />
        ) : (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="customer"
            mode="manage"
          />
        ))}
    </div>
  )
}

function CustomerRow({
  customer,
  isAdmin,
  selected,
  onSelect,
  onEdit,
  onManageAccess,
}: {
  customer: Customer
  isAdmin: boolean
  selected: boolean
  onSelect: () => void
  onEdit: () => void
  onManageAccess: () => void
}) {
  const queryClient = useQueryClient()
  const archive = useMutation({
    mutationFn: () =>
      api.updateCustomer(customer.id, {
        archived: !customer.archived,
        baseVersion: customer.version,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      notify.success(customer.archived ? 'Kunde reaktiviert' : 'Kunde archiviert')
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const remove = useMutation({
    mutationFn: () => api.deleteCustomer(customer.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      notify.success('Kunde gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <tr
      className={`group transition-colors hover:bg-bg-overlay/40 ${
        customer.archived ? 'opacity-60' : ''
      } ${selected ? 'bg-accent/5' : ''}`}
    >
      {isAdmin && (
        <td className="pl-5 pr-2 py-3">
          <RowCheckbox
            checked={selected}
            onToggle={onSelect}
            label={`${customer.name} auswählen`}
          />
        </td>
      )}
      <td className="px-5 py-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-text-primary truncate">
          {customer.restricted && (
            <span title="Beschränkter Zugriff" className="shrink-0">
              <Lock className="h-3 w-3 text-accent" aria-label="Beschränkter Zugriff" />
            </span>
          )}
          <span className="truncate">{customer.name}</span>
        </p>
        {customer.notes && (
          <p className="mt-0.5 text-xs text-text-tertiary truncate max-w-md">{customer.notes}</p>
        )}
      </td>
      <td className="px-5 py-3 text-sm text-text-secondary">{customer.billingCity ?? '—'}</td>
      <td className="px-5 py-3 text-sm font-mono text-text-secondary">
        {customer.billingVatId ?? '—'}
      </td>
      <td className="px-5 py-3 text-xs text-text-tertiary tabular-nums">
        {new Date(customer.createdAt).toLocaleDateString('de-DE')}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onEdit}
            aria-label="Bearbeiten"
            className="text-text-tertiary hover:text-accent"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onManageAccess}
              aria-label="Freigabe verwalten"
              title="Freigabe verwalten"
              className={
                customer.restricted
                  ? 'text-accent hover:text-accent'
                  : 'text-text-tertiary hover:text-accent'
              }
            >
              <Lock className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => archive.mutate()}
            aria-label={customer.archived ? 'Reaktivieren' : 'Archivieren'}
            className="text-text-tertiary hover:text-accent"
          >
            {customer.archived ? (
              <ArchiveRestore className="h-4 w-4" />
            ) : (
              <Archive className="h-4 w-4" />
            )}
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={async () => {
                const ok = await confirm({
                  title: `„${customer.name}“ löschen?`,
                  description:
                    'Projekte werden mitgelöscht, Time-Entries verlieren ihren Project-Bezug.',
                  variant: 'danger',
                  confirmLabel: 'Löschen',
                })
                if (ok) remove.mutate()
              }}
              aria-label="Löschen"
              className="text-text-tertiary hover:text-danger"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

export function KundenFirmaFeld({
  firmaId,
  firmen,
  onChange,
  ladezustand,
}: {
  firmaId: string
  firmen: FibuFirma[]
  onChange: (firmaId: string) => void
  ladezustand?: 'laedt' | 'fehler'
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor="cust-firma">Firma in emberfibu</Label>
      <Select
        id="cust-firma"
        value={firmaId}
        disabled={!!ladezustand}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {ladezustand ? 'Zuordnung noch nicht geladen' : 'Keine feste Zuordnung'}
        </option>
        {firmen.map((firma) => (
          <option key={firma.id} value={firma.id}>
            {firma.name}
          </option>
        ))}
      </Select>
      <p className="text-xs text-text-tertiary">
        {ladezustand === 'fehler'
          ? 'Firmenzuordnung konnte nicht geladen werden. Beim Speichern bleibt sie unverändert.'
          : ladezustand === 'laedt'
            ? 'Firmenzuordnung wird geladen. Andere Kundendaten können bereits gespeichert werden.'
            : 'Diese Firma wird bei der nächsten Abrechnung vorgeschlagen.'}
      </p>
    </div>
  )
}

function CustomerDialog({
  open,
  customer,
  isAdmin,
  onClose,
}: {
  open: boolean
  customer: Customer | null
  isAdmin: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const active = useRef(true)
  const isEdit = !!customer
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  // #1007: Kundennummer = Schlüssel zum Kontakt in emberfibu. Leer beim
  // Anlegen -> der Server vergibt die nächste K-Nummer.
  const [kundennummer, setKundennummer] = useState('')
  // undefined = unverändert; '' = ausdrücklich entfernte Zuordnung.
  const [firmaId, setFirmaId] = useState<string>()
  const [creationAttempt] = useState(
    () => new CreationAttempt<Parameters<typeof api.createCustomer>[0]>(),
  )
  const { data: fibuStatus } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: open && isAdmin,
  })
  const fibuAktiv = isAdmin && !!fibuStatus && fibuStatus.modus !== 'aus'
  const { data: kundenFirma, isError: firmaLadefehler } = useQuery({
    queryKey: ['fibu-kunden-firma', customer?.id],
    queryFn: () => api.fibuKundenFirma(customer?.id ?? ''),
    enabled: open && fibuAktiv && !!customer,
  })
  const [billing, setBilling] = useState({
    contactName: '',
    email: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
    vatId: '',
    buyerReference: '',
  })
  const [showBilling, setShowBilling] = useState(false)

  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const close = () => {
    active.current = false
    onClose()
  }

  useEffect(() => {
    if (!open) return
    setFirmaId(undefined)
    creationAttempt.reset()
    if (customer) {
      setName(customer.name)
      setNotes(customer.notes ?? '')
      setKundennummer(customer.kundennummer ?? '')
      setBilling({
        contactName: customer.billingContactName ?? '',
        email: customer.billingEmail ?? '',
        street: customer.billingStreet ?? '',
        zip: customer.billingZip ?? '',
        city: customer.billingCity ?? '',
        country: customer.billingCountry ?? 'DE',
        vatId: customer.billingVatId ?? '',
        buyerReference: customer.buyerReference ?? '',
      })
      setShowBilling(
        Boolean(
          customer.billingStreet ||
            customer.billingZip ||
            customer.billingCity ||
            customer.billingVatId,
        ),
      )
    } else {
      setName('')
      setNotes('')
      setKundennummer('')
      setBilling({
        contactName: '',
        email: '',
        street: '',
        zip: '',
        city: '',
        country: 'DE',
        vatId: '',
        buyerReference: '',
      })
      setShowBilling(false)
    }
  }, [open, customer, creationAttempt])

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        notes: notes || null,
        billingContactName: billing.contactName || null,
        billingEmail: billing.email || null,
        billingStreet: billing.street || null,
        billingZip: billing.zip || null,
        billingCity: billing.city || null,
        billingCountry: billing.country || 'DE',
        billingVatId: billing.vatId || null,
        buyerReference: billing.buyerReference || null,
      }
      const nr = kundennummer.trim()
      const complete = {
        ...payload,
        ...(nr ? { kundennummer: nr } : {}),
        ...(fibuAktiv && firmaId !== undefined ? { firmaId: firmaId || null } : {}),
        ...(customer ? { baseVersion: customer.version } : {}),
      }
      return creationAttempt.run(complete, (data, key, revision) =>
        customer
          ? api.updateCustomer(
              customer.id,
              { ...data, kundennummer: data.kundennummer ?? undefined },
              key,
              revision,
            )
          : api.createCustomer(data, key, revision),
      )
    },
    // A closed form still owns its submitted save. Refresh shared data even
    // after an error or a committed response lost in transit.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
        queryClient.invalidateQueries({ queryKey: ['fibu-ziel'] }),
        queryClient.invalidateQueries({ queryKey: ['fibu-kunden-firma'] }),
      ]),
    onSuccess: () => {
      if (!active.current) return
      notify.success(isEdit ? 'Kunde aktualisiert' : 'Kunde angelegt')
      close()
    },
    onError: (err: Error) => {
      if (active.current) notify.error(err.message)
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || save.isPending) return
    save.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={close}
      title={isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}
      footer={
        <>
          <Button variant="ghost" onClick={close} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={!name.trim() || save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        {save.isError && (
          <p role="alert" className="text-sm text-warning">
            {save.error?.message}
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="cust-name">Name</Label>
          <Input
            id="cust-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Acme GmbH"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cust-kundennummer">Kundennummer</Label>
          <Input
            id="cust-kundennummer"
            value={kundennummer}
            onChange={(e) => setKundennummer(e.target.value)}
            placeholder={isEdit ? '' : 'leer = wird automatisch vergeben'}
            maxLength={100}
          />
          <p className="text-xs text-text-tertiary">
            Schlüssel zum Kontakt in emberfibu — dort muss es einen Kunden mit genau dieser Nummer
            geben.
          </p>
        </div>
        {fibuAktiv && (
          <KundenFirmaFeld
            firmaId={firmaId ?? (customer ? kundenFirma?.firmaId : '') ?? ''}
            firmen={fibuStatus?.firmen ?? []}
            onChange={setFirmaId}
            ladezustand={
              customer && !kundenFirma ? (firmaLadefehler ? 'fehler' : 'laedt') : undefined
            }
          />
        )}
        <div className="space-y-2">
          <Label htmlFor="cust-notes">Notizen (optional)</Label>
          <Input
            id="cust-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Hauptkontakt, Branche, …"
          />
        </div>

        <div className="border-t border-border-subtle pt-3">
          <button
            type="button"
            onClick={() => setShowBilling((v) => !v)}
            className="text-sm text-text-secondary hover:text-accent transition-colors"
          >
            {showBilling ? '− Rechnungsadresse ausblenden' : '+ Rechnungsadresse / E-Rechnung'}
          </button>
        </div>

        {showBilling && (
          <div className="space-y-3 rounded-lg border border-border-subtle bg-bg-overlay/30 p-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Ansprechpartner</Label>
                <Input
                  value={billing.contactName}
                  onChange={(e) => setBilling({ ...billing, contactName: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={billing.email}
                  onChange={(e) => setBilling({ ...billing, email: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Straße + Nr.</Label>
              <Input
                value={billing.street}
                onChange={(e) => setBilling({ ...billing, street: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>PLZ</Label>
                <Input
                  value={billing.zip}
                  onChange={(e) => setBilling({ ...billing, zip: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Ort</Label>
                <Input
                  value={billing.city}
                  onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Land</Label>
                <Input
                  value={billing.country}
                  onChange={(e) => setBilling({ ...billing, country: e.target.value })}
                  placeholder="DE"
                  maxLength={2}
                />
              </div>
              <div className="space-y-1">
                <Label>USt-IdNr</Label>
                <Input
                  value={billing.vatId}
                  onChange={(e) => setBilling({ ...billing, vatId: e.target.value })}
                  placeholder="DE123456789"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Bestellnr. / Leitweg-ID</Label>
              <Input
                value={billing.buyerReference}
                onChange={(e) => setBilling({ ...billing, buyerReference: e.target.value })}
                placeholder="optional, B2G: Leitweg-ID"
              />
            </div>
          </div>
        )}
      </form>
    </SidePanel>
  )
}
